import React, { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { useModalWidth } from '../hooks/useModalWidth';

export function PaymentAmountModal({ 
  isOpen, 
  onClose, 
  onPaymentTypeSelected,
  productType,
  totalAmount,
  currency = 'GHS'
}) {
  const modalWidth = useModalWidth();
  const [paymentType, setPaymentType] = useState('full'); // 'full' or 'partial'
  const [partialAmount, setPartialAmount] = useState('');
  const [amountError, setAmountError] = useState('');

  // Debug logging
  console.log('PaymentAmountModal props:', { isOpen, productType, totalAmount, currency });

  const handleAmountChange = (value) => {
    // Prevent non-numeric characters and ensure proper decimal format
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return; // Don't update if multiple decimal points
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      return; // Don't update if more than 2 decimal places
    }
    
    setPartialAmount(cleanValue);
    
    // Real-time validation
    const numValue = parseFloat(cleanValue);
    if (cleanValue && (isNaN(numValue) || numValue < 1)) {
      setAmountError('Minimum payment amount is GHS 1.00');
    } else if (cleanValue && numValue > parseFloat(totalAmount)) {
      setAmountError(`Amount cannot exceed ${formatCurrency(totalAmount)}`);
    } else {
      setAmountError('');
    }
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (paymentType === 'full') {
      onPaymentTypeSelected({
        type: 'full',
        amount: totalAmount,
        currency: currency
      });
    } else {
      const numAmount = parseFloat(partialAmount);
      
      if (!partialAmount || isNaN(numAmount) || numAmount <= 0) {
        setAmountError('Please enter a valid amount');
        return;
      }
      
      if (numAmount < 1) {
        setAmountError('Minimum payment amount is GHS 1.00');
        return;
      }
      
      if (numAmount > parseFloat(totalAmount)) {
        setAmountError(`Amount cannot exceed ${formatCurrency(totalAmount)}`);
        return;
      }
      
      onPaymentTypeSelected({
        type: 'partial',
        amount: numAmount,
        currency: currency
      });
    }
    onClose();
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'GHS 0';
    const num = Number(String(amount).replace(/[^\d.]/g, ''));
    if (isNaN(num)) return `GHS ${amount}`;
    return `GHS ${num.toLocaleString()}`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          minWidth: 320,
          maxWidth: modalWidth,
          width: '90%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px 0 rgba(0,0,0,0.13)',
          padding: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 20px 32px' }}>
          <div className="modal-header-title" style={{ 
            fontWeight: 800, 
            color: '#222', 
            marginBottom: 6, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between' 
          }}>
            <span>Payment Amount</span>
            <button 
              onClick={onClose} 
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 22, 
                color: '#888', 
                cursor: 'pointer', 
                marginLeft: 12, 
                fontWeight: 700, 
                lineHeight: 1 
              }} 
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="modal-header-desc" style={{ color: '#888', marginBottom: 0, marginTop: 0 }}>
            Choose payment amount for your {productType} policy
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '0 32px 32px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Total Amount Display */}
          <div style={{
            background: '#f8f9fa',
            padding: '16px 20px',
            borderRadius: 12,
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Total Amount Due</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#00a651' }}>
              {formatCurrency(totalAmount)}
            </div>
          </div>

          {/* Payment Type Selection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 4 }}>
              Select Payment Type
            </div>
            
            {/* Full Payment Option */}
            <button
              onClick={() => setPaymentType('full')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                border: paymentType === 'full' ? '2px solid #00a651' : '1px solid #ddd',
                borderRadius: 12,
                background: paymentType === 'full' ? '#f6ffed' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 2 }}>
                  Full Payment
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  Pay the complete amount of {formatCurrency(totalAmount)}
                </div>
              </div>
            </button>

            {/* Partial Payment Option */}
            <button
              onClick={() => setPaymentType('partial')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                border: paymentType === 'partial' ? '2px solid #00a651' : '1px solid #ddd',
                borderRadius: 12,
                background: paymentType === 'partial' ? '#f6ffed' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 2 }}>
                  Partial Payment
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  Pay a specific amount (minimum: {formatCurrency(1)})
                </div>
              </div>
            </button>
          </div>

          {/* Partial Amount Input */}
          {paymentType === 'partial' && (
            <div style={{
              padding: '16px 20px',
              background: '#f8f9fa',
              borderRadius: 12,
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                Enter Amount
              </div>
              <div className="payment-amount-input-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  value={partialAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="payment-amount-input"
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    padding: '10px 12px',
                    border: amountError ? '1px solid #ff4d4f' : '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    background: '#fff',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              {amountError && (
                <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 4 }}>
                  {amountError}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Amount: {partialAmount ? formatCurrency(partialAmount) : 'GHS 0.00'}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={handleSubmit}
              className={`btn ${amountError ? 'btn-secondary' : 'btn-primary'} w-50 mobile-full-width`}
              disabled={paymentType === 'partial' && amountError}
              style={{
                borderRadius: '50px',
                padding: '14px 20px',
                fontSize: 15,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: paymentType === 'partial' && amountError ? 0.6 : 1,
                cursor: paymentType === 'partial' && amountError ? 'not-allowed' : 'pointer',
              }}
            >
              <CreditCard size={18} />
              Proceed
            </button>
            <button
              onClick={onClose}
              className="btn btn-white w-50 mobile-full-width"
              style={{
                borderRadius: '50px',
                padding: '14px 20px',
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
